import { Component, signal, computed, inject, viewChild, TemplateRef, DestroyRef, ElementRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import {
  StandardReviewImportValidationService,
  ValidationResult,
} from '../../core/services/standard-review-import-validation.service';

type ToastAction = 'import' | 'download';
type ToastVariant = 'success' | 'error';

interface ToastData {
  title: ToastVariant;
  header: string;
  content: string;
  link?: { text: string; route: string[] };
}

@Component({
  selector: 'app-import-standard-review',
  standalone: true,
  imports: [RouterLink, NgClass, MatDialogModule],
  templateUrl: './import-standard-review.html',
  styleUrl: './import-standard-review.css',
})

export class ImportStandardReview {
  private readonly validationService = inject(StandardReviewImportValidationService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  isValidating = signal(false);
  isDownloading = signal(false);
  selectedFileName: string | null = null;
  validationResult = signal<ValidationResult | null>(null);
  hasImportErrors = computed(() => this.validationResult()?.validation === 'failed');

  private readonly importReviewErrors = computed(() => this.validationResult()?.errors ?? []);

  // Single line errors have no row/field
  singleLineErrors = computed(() =>
    this.importReviewErrors().filter((error) => error.row === undefined && error.field === undefined)
  );

  // Row level errors carry a row/field
  rowLevelErrors = computed(() =>
    this.importReviewErrors().filter((error) => error.row !== undefined || error.field !== undefined)
  );

  private readonly importReviewToast =
    viewChild.required<TemplateRef<ToastData>>('importReviewToast');
  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  private readonly toastMessages: Record<
    ToastAction,
    Record<ToastVariant, Pick<ToastData, 'header' | 'content' | 'link'>>
  > = {
    import: {
      success: {
        header: 'Upload Successful!',
        content: '',
        link: { text: 'Go to Review Listing', route: ['/review-management'] },
      },
      error: {
        header: 'Upload Failed!',
        content: 'Please Resolve Error(s) and Retry',
      },
    },
    download: {
      success: {
        header: 'Download Successful!',
        content: 'Template downloaded successfully.',
      },
      error: {
        header: 'Download Failed!',
        content: 'Please try again.',
      },
    },
  };

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.selectedFileName = file.name;

    this.isValidating.set(true);
    try {
      const result = await this.validationService.validateStandardReviewUpload(file);
      this.validationResult.set(result);
      this.openToast('import', result.validation === 'success' ? 'success' : 'error', result.createdCount);
    } finally {
      this.isValidating.set(false);
      this.clearFile();
    }
  }

  downloadTemplate(): void {
    if (this.isDownloading()) return;
    this.isDownloading.set(true);
    fetch('assets/documents/Bulk_Upload_STD_Review_Template.xlsx')
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch template: ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Bulk_Upload_STD_Review_Template.xlsx';
        link.click();
        URL.revokeObjectURL(url);
        this.openToast('download', 'success');
      })
      .catch(err => {
        console.error(err);
        this.openToast('download', 'error');
      })
      .finally(() => this.isDownloading.set(false));
  }

  clearFile() {
    this.selectedFileName = null;
    this.fileInput().nativeElement.value = '';
  }

  openToast(action: ToastAction, variant: ToastVariant, createdCount?: number): void {
    const { header, link } = this.toastMessages[action][variant];
    const content = (action === 'import' && variant === 'success' && createdCount !== undefined)
      ? `${createdCount} Standard Review${createdCount > 1 ? 's' : ''} ${createdCount > 1 ? 'have' : 'has'} been created in Draft state.`
      : this.toastMessages[action][variant].content;

    const ref = this.dialog.open(this.importReviewToast(), {
      data: { title: variant, header, content, link } satisfies ToastData,
      width: '402px',
      height: '101px',
      panelClass: 'import-review-toast',
      hasBackdrop: true,
      position: { top: '120px', right: '28px' },
      autoFocus: false,
    });

    // Auto-close the toast after 10 seconds 
    const autoCloseTimer = setTimeout(() => ref.close(), 10000);
    const unregisterOnDestroy = this.destroyRef.onDestroy(() => {
      clearTimeout(autoCloseTimer);
      ref.close();
    });

    ref.afterClosed().subscribe(() => {
      clearTimeout(autoCloseTimer);
      unregisterOnDestroy();
    });
  }
}
